import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pendingTradesAPI, accountsAPI } from '../services/api';
import toast from 'react-hot-toast';
import { CheckCircle, XCircle, Edit, Trash2, Clock, AlertCircle, AlertTriangle } from 'lucide-react';

const PendingTrades: React.FC = () => {
  const queryClient = useQueryClient();
  const [editingTrade, setEditingTrade] = useState<any>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({
    symbol: '',
    quantity: 0,
    price: 0,
    account_ids: [] as number[],
  });
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingTradeId, setRejectingTradeId] = useState<number | null>(null);

  const { data: pendingTrades, isLoading } = useQuery({
    queryKey: ['pendingTrades'],
    queryFn: () => pendingTradesAPI.list({ status: 'PENDING' }).then((res) => res.data),
    refetchInterval: 5000, // Auto-refresh every 5 seconds
  });

  const { data: accounts } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsAPI.list().then((res) => res.data),
  });

  const approveMutation = useMutation({
    mutationFn: ({ id, modifiedData }: { id: number; modifiedData?: any }) =>
      pendingTradesAPI.approve(id, modifiedData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingTrades'] });
      queryClient.invalidateQueries({ queryKey: ['trades'] });
      queryClient.invalidateQueries({ queryKey: ['tradeStats'] });
      toast.success('Trade approved and executed!');
      setShowEditModal(false);
      setEditingTrade(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to approve trade');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      pendingTradesAPI.reject(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingTrades'] });
      toast.success('Trade rejected');
      setShowRejectModal(false);
      setRejectingTradeId(null);
      setRejectionReason('');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to reject trade');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: pendingTradesAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingTrades'] });
      toast.success('Trade deleted');
    },
  });

  const cancelAllMutation = useMutation({
    mutationFn: () => pendingTradesAPI.cancelAll(),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['pendingTrades'] });
      toast.success(res.data?.message || 'All pending trades cancelled');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to cancel pending trades');
    },
  });

  const handleQuickApprove = (trade: any) => {
    if (window.confirm(`Approve trade: ${trade.action} ${trade.quantity} ${trade.symbol}?`)) {
      approveMutation.mutate({ id: trade.id });
    }
  };

  const handleEditApprove = (trade: any) => {
    setEditingTrade(trade);
    setEditData({
      symbol: trade.symbol,
      quantity: trade.quantity,
      price: trade.price || 0,
      account_ids: trade.account_ids,
    });
    setShowEditModal(true);
  };

  const handleSaveAndApprove = () => {
    if (!editingTrade) return;

    const modifiedData: any = {};

    if (editData.symbol !== editingTrade.symbol) {
      modifiedData.symbol = editData.symbol;
    }
    if (editData.quantity !== editingTrade.quantity) {
      modifiedData.quantity = editData.quantity;
    }
    if (editData.price !== (editingTrade.price || 0)) {
      modifiedData.price = editData.price;
    }
    if (JSON.stringify(editData.account_ids) !== JSON.stringify(editingTrade.account_ids)) {
      modifiedData.account_ids = editData.account_ids;
    }

    approveMutation.mutate({
      id: editingTrade.id,
      modifiedData: Object.keys(modifiedData).length > 0 ? modifiedData : undefined,
    });
  };

  const handleReject = (tradeId: number) => {
    setRejectingTradeId(tradeId);
    setShowRejectModal(true);
  };

  const handleConfirmReject = () => {
    if (rejectingTradeId) {
      rejectMutation.mutate({
        id: rejectingTradeId,
        reason: rejectionReason || undefined,
      });
    }
  };

  const toggleAccount = (accountId: number) => {
    setEditData((prev) => ({
      ...prev,
      account_ids: prev.account_ids.includes(accountId)
        ? prev.account_ids.filter((id) => id !== accountId)
        : [...prev.account_ids, accountId],
    }));
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const getExpiryWarning = (expiresAt: string) => {
    const expires = new Date(expiresAt);
    const now = new Date();
    const hoursLeft = Math.floor((expires.getTime() - now.getTime()) / (1000 * 60 * 60));

    if (hoursLeft < 2) {
      return { color: 'text-red-600', message: `Expires in ${hoursLeft}h!` };
    } else if (hoursLeft < 6) {
      return { color: 'text-yellow-600', message: `Expires in ${hoursLeft}h` };
    }
    return { color: 'text-gray-600', message: `Expires in ${hoursLeft}h` };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading pending trades...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Pending Trades</h1>
          <p className="text-gray-600 mt-1">
            Trades awaiting your approval{' '}
            {pendingTrades && pendingTrades.length > 0 && (
              <span className="text-blue-600 font-semibold">
                ({pendingTrades.length} pending)
              </span>
            )}
          </p>
        </div>
        {pendingTrades && pendingTrades.length > 0 && (
          <button
            onClick={() => {
              if (window.confirm(`Cancel all ${pendingTrades.length} pending trade(s)?`)) {
                cancelAllMutation.mutate();
              }
            }}
            disabled={cancelAllMutation.isPending}
            className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 whitespace-nowrap"
          >
            <XCircle size={18} />
            {cancelAllMutation.isPending ? 'Cancelling...' : 'Cancel All'}
          </button>
        )}
      </div>

      {/* Alert if no pending trades */}
      {(!pendingTrades || pendingTrades.length === 0) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <AlertCircle className="mx-auto text-blue-500 mb-2" size={48} />
          <h3 className="text-lg font-semibold text-blue-900 mb-1">No Pending Trades</h3>
          <p className="text-blue-700">
            Trades requiring approval will appear here when webhooks are received in manual mode.
          </p>
        </div>
      )}

      {/* Pending Trades List */}
      {pendingTrades && pendingTrades.length > 0 && (
        <div className="space-y-4">
          {pendingTrades.map((trade: any) => {
            const expiry = getExpiryWarning(trade.expires_at);
            return (
              <div
                key={trade.id}
                className="bg-white rounded-lg shadow border-l-4 border-yellow-500 p-6"
              >
                <div className="flex items-start justify-between">
                  {/* Trade Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span
                        className={`text-2xl font-bold ${
                          trade.action === 'BUY' ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {trade.action}
                      </span>
                      <span className="text-2xl font-bold text-gray-900">
                        {trade.symbol}
                      </span>
                      <span className="text-lg text-gray-600">x{trade.quantity}</span>
                      {trade.price && (
                        <span className="text-sm text-gray-500">@ ₹{trade.price}</span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Order Type</p>
                        <p className="font-medium">{trade.order_type}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Product</p>
                        <p className="font-medium">{trade.product}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Exchange</p>
                        <p className="font-medium">{trade.exchange}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Accounts</p>
                        <p className="font-medium">{trade.account_ids.length} selected</p>
                      </div>
                    </div>

                    {/* Current Holdings Alert */}
                    {trade.action === 'BUY' && trade.current_holdings && Object.keys(trade.current_holdings).length > 0 && (
                      <div className="mt-4 bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="text-orange-600 mt-0.5" size={18} />
                          <div className="flex-1">
                            <p className="font-semibold text-orange-900 mb-2">
                              ⚠️ Adding to Existing Position
                            </p>
                            <p className="text-sm text-orange-800 mb-2">
                              You currently hold <strong>{trade.symbol}</strong> in{' '}
                              {Object.keys(trade.current_holdings).length} account(s):
                            </p>
                            <div className="space-y-1">
                              {Object.entries(trade.current_holdings).map(([accountId, quantity]: [string, any]) => (
                                <div key={accountId} className="text-sm text-orange-800 flex items-center gap-2">
                                  <span className="font-mono bg-orange-100 px-2 py-0.5 rounded">
                                    Account #{accountId}
                                  </span>
                                  <span>currently holds</span>
                                  <span className="font-bold">{quantity} shares</span>
                                  <span className="text-orange-600">
                                    → will become {quantity + trade.quantity} shares
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* New Position Badge */}
                    {trade.action === 'BUY' && (!trade.current_holdings || Object.keys(trade.current_holdings).length === 0) && (
                      <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="text-green-600" size={16} />
                          <p className="text-sm text-green-800 font-medium">
                            ✨ New Position - Currently holding 0 shares
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-4 mt-4 text-sm">
                      <span className="flex items-center gap-1 text-gray-600">
                        <Clock size={14} />
                        {getTimeAgo(trade.created_at)}
                      </span>
                      <span className={`flex items-center gap-1 ${expiry.color}`}>
                        <AlertCircle size={14} />
                        {expiry.message}
                      </span>
                      <span className="text-gray-500">#{trade.id}</span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-2 ml-4">
                    <button
                      onClick={() => handleQuickApprove(trade)}
                      disabled={approveMutation.isPending}
                      className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 whitespace-nowrap"
                    >
                      <CheckCircle size={18} />
                      Quick Approve
                    </button>

                    <button
                      onClick={() => handleEditApprove(trade)}
                      className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 whitespace-nowrap"
                    >
                      <Edit size={18} />
                      Edit & Approve
                    </button>

                    <button
                      onClick={() => handleReject(trade.id)}
                      disabled={rejectMutation.isPending}
                      className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 whitespace-nowrap"
                    >
                      <XCircle size={18} />
                      Reject
                    </button>

                    <button
                      onClick={() => {
                        if (window.confirm('Delete this pending trade?')) {
                          deleteMutation.mutate(trade.id);
                        }
                      }}
                      className="flex items-center gap-2 text-gray-600 hover:text-gray-900 px-4 py-2 whitespace-nowrap"
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingTrade && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-900">Edit Trade Before Approval</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Symbol
                  </label>
                  <input
                    type="text"
                    value={editData.symbol}
                    onChange={(e) => setEditData({ ...editData, symbol: e.target.value.toUpperCase() })}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity
                  </label>
                  <input
                    type="number"
                    value={editData.quantity}
                    onChange={(e) => setEditData({ ...editData, quantity: parseInt(e.target.value) })}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>

              {editingTrade.order_type === 'LIMIT' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price (₹)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={editData.price}
                    onChange={(e) => setEditData({ ...editData, price: parseFloat(e.target.value) })}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Accounts
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {accounts?.map((account: any) => (
                    <label
                      key={account.id}
                      className={`flex items-center p-3 rounded-lg border-2 cursor-pointer ${
                        editData.account_ids.includes(account.id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={editData.account_ids.includes(account.id)}
                        onChange={() => toggleAccount(account.id)}
                        className="mr-3"
                      />
                      <div>
                        <p className="font-medium text-sm">{account.account_name}</p>
                        <p className="text-xs text-gray-500">{account.kite_user_id}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAndApprove}
                disabled={approveMutation.isPending}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {approveMutation.isPending ? 'Approving...' : 'Save & Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">Reject Trade</h2>
              <button
                onClick={() => setShowRejectModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason (optional)
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Why are you rejecting this trade?"
                rows={3}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500"
              />
            </div>

            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReject}
                disabled={rejectMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {rejectMutation.isPending ? 'Rejecting...' : 'Reject Trade'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingTrades;
